import { createContext, useContext, useEffect, useState, type ReactNode } from "react";
import { getTelegramWebApp, hasTelegramWebApp, type TelegramUser } from "./webApp";
import { getOrCreateAnonymousId } from "../shared/anonymousId";

export interface TelegramContextValue {
  isTelegram: boolean;
  user: TelegramUser | null;
  startParam: string | null;
  initDataRaw: string | null;
  anonymousId: string | null;
  colorScheme: "light" | "dark";
}

const TelegramContext = createContext<TelegramContextValue | null>(null);

/**
 * Builds the context value for a confirmed Telegram WebApp object. `initData`
 * can legitimately be empty even inside a real Telegram session (observed on
 * Telegram Desktop) — when that happens we still mark the session as
 * Telegram (so ready()/theme/haptics apply) but fall back to the same
 * anonymous identity the browser demo uses, so API calls keep working
 * instead of failing with no identity at all.
 */
function buildTelegramValue(webApp: NonNullable<ReturnType<typeof getTelegramWebApp>>): TelegramContextValue {
  const hasInitData = webApp.initData.length > 0;
  return {
    isTelegram: true,
    user: webApp.initDataUnsafe.user ?? null,
    startParam: webApp.initDataUnsafe.start_param ?? null,
    initDataRaw: hasInitData ? webApp.initData : null,
    anonymousId: hasInitData ? null : getOrCreateAnonymousId(),
    colorScheme: webApp.colorScheme ?? "dark",
  };
}

function buildBrowserValue(): TelegramContextValue {
  return {
    isTelegram: false,
    user: null,
    startParam: null,
    initDataRaw: null,
    anonymousId: getOrCreateAnonymousId(),
    colorScheme: "dark",
  };
}

const LATE_DETECTION_POLL_MS = 150;
const LATE_DETECTION_MAX_ATTEMPTS = 67; // ~10s — a slow-loading bridge script must never block startup, but also must not be given up on too early

export function TelegramProvider({ children }: { children: ReactNode }) {
  const [value, setValue] = useState<TelegramContextValue>(() => {
    const webApp = getTelegramWebApp();
    return webApp ? buildTelegramValue(webApp) : buildBrowserValue();
  });

  useEffect(() => {
    // This effect's ONLY job is upgrading the context value (user/initData)
    // once the bridge is available — it must never gate or delay rendering
    // children, and it must never call the Telegram readiness signal
    // (see ./readySignal.ts, triggered from WelcomeScreen instead — calling
    // ready() here would fire it before app UI has painted, which is the
    // proven cause of a prior black-screen regression on real Telegram
    // clients).
    if (hasTelegramWebApp()) return;

    // Not present yet: the bridge script may still be loading. Poll rather
    // than permanently treating this as browser mode — root rendering has
    // already happened either way, so this never blocks the UI, it only
    // upgrades the Telegram context once/if it shows up.
    let attempts = 0;
    const interval = setInterval(() => {
      attempts += 1;
      const webApp = getTelegramWebApp();
      if (webApp) {
        clearInterval(interval);
        setValue(buildTelegramValue(webApp));
      } else if (attempts >= LATE_DETECTION_MAX_ATTEMPTS) {
        clearInterval(interval);
      }
    }, LATE_DETECTION_POLL_MS);

    return () => clearInterval(interval);
  }, []);

  return <TelegramContext.Provider value={value}>{children}</TelegramContext.Provider>;
}

export function useTelegram(): TelegramContextValue {
  const ctx = useContext(TelegramContext);
  if (!ctx) throw new Error("useTelegram must be used within TelegramProvider");
  return ctx;
}
